import { IBatchjob } from "@mctypes/batchjob.types";
import { IBim360FolderContent } from "@mctypes/global.types";
import { Box, Flex, Paper, Stack, Title } from "@mantine/core";
import React from "react";
import { SegmentedToggle, VSpace } from "../../../../../components";
import BIM360AgFolderAndFileExplorer from "../../../../../components/Bim360AgFolderAndFileExplorer/BIM360AgFolderAndFileExplorer";
import CheckOrWarn from "../../../../../components/CheckOrWarn";
import RevitFilesExportTable from "../../../../../components/RevitFilesExportTable/RevitFilesExportTable";
import { useAppSelector } from "../../../../../hooks";

const Bim360RevitFileSelectorCard: React.FC<{
  projectId: string;
  bim360ProjectId: string;
  updateFiles: (files: IBatchjob.IBim360RevitExportFile[]) => void;
  updateRevitViewValue: (fileId: string, revitViewValue: string) => void;
  isValid: boolean;
}> = ({ projectId, bim360ProjectId, updateFiles, updateRevitViewValue, isValid }) => {
  enum TabsEnum {
    SELECTED_FILES = "Valgte filer",
    SELECT_FILES_FROM_BIM360 = "Velg filer fra ACC",
  }
  const [selectedTab, setSelectedTab] = React.useState<TabsEnum>(TabsEnum.SELECTED_FILES);

  let bim360RevitExportFiles = useAppSelector(
    (state) => state.batchJobReducer.activeBatchJob?.bim360Settings?.bim360RevitExportFiles,
  );

  const updateFilesFromBim360 = (filesFromBim360Editor: IBim360FolderContent[]) => {
    const filesToKeep = bim360RevitExportFiles.filter(
      (revitFile) =>
        filesFromBim360Editor.findIndex((_f) => _f.urn === revitFile.bim360Urn || _f.id === revitFile.bim360Urn) !== -1,
    );
    const newFiles = filesFromBim360Editor.filter(
      (fileFromBim360) =>
        bim360RevitExportFiles.findIndex(
          (revitFile) => revitFile.bim360Urn === fileFromBim360.urn || revitFile.bim360Urn === fileFromBim360.id,
        ) === -1,
    );

    const newFilesFormatted: IBatchjob.IBim360RevitExportFile[] = newFiles.map((bim360File) => {
      return {
        bim360Urn: bim360File.urn || bim360File.id,
        name: bim360File.name,
        revitView: {
          value: "",
          match: "EXACT",
        },
        useMultipleFilesPerViewExport: false,
      };
    });

    updateFiles([...filesToKeep, ...newFilesFormatted]);

    setTimeout(() => {
      setSelectedTab(TabsEnum.SELECTED_FILES);
    }, 100);
  };

  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff" style={{ display: "flex", height: "600px" }}>
      <Stack gap="xs" h="100%">
        <Title order={4} fw={400}>
          <CheckOrWarn bool={isValid} />
          Revit-filer
        </Title>
        <Title order={6} fw={400} c="dimmed">
          Velg revit-filer fra ACC for å eksportere til IFC
        </Title>
        <Flex>
          <h5 style={{ background: "#fff3cd", color: "#857e6b" }}>
            (Advarsel! IFC-eksport bør kun anvendes med filer lagret i Revit 2024 eller nyere versjoner)
          </h5>
        </Flex>
        <Flex direction="column" h="100%" style={{ overflow: "auto" }} w="100%">
          <Box w="300px" maw="100%">
            <SegmentedToggle
              value={selectedTab}
              onChange={(tab) => setSelectedTab(tab as TabsEnum)}
              options={[
                { value: TabsEnum.SELECTED_FILES, label: "Valgte filer" },
                { value: TabsEnum.SELECT_FILES_FROM_BIM360, label: "Velg filer fra ACC" },
              ]}
            />
          </Box>
          <VSpace />
          <Box h="100%" style={{ overflow: "auto" }}>
            {selectedTab === TabsEnum.SELECT_FILES_FROM_BIM360 && (
              <BIM360AgFolderAndFileExplorer
                filterExtension={{
                  label: "Revit",
                  value: ".rvt",
                }}
                projectId={projectId}
                bim360ProjectId={bim360ProjectId}
                includeSelectedFilesHandler
                onSave={(files) => updateFilesFromBim360(files)}
                selectedAgFiles={bim360RevitExportFiles?.map((file) => {
                  return {
                    id: file.bim360Urn,
                    name: file.name,
                  };
                })}
                onAbort={() => setSelectedTab(TabsEnum.SELECTED_FILES)}
              />
            )}
            {selectedTab === TabsEnum.SELECTED_FILES && (
              <RevitFilesExportTable
                includeRevitView={true}
                revitFilesToExport={bim360RevitExportFiles}
                updateFile={(fileId, revitViewValue) => updateRevitViewValue(fileId, revitViewValue)}
              />
            )}
          </Box>
        </Flex>
      </Stack>
    </Paper>
  );
};

export default Bim360RevitFileSelectorCard;
